import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Calculator, ChevronDown, ChevronUp, Check } from 'lucide-react';

interface MathKeyboardProps {
  onInsert: (text: string) => void;
  onTransfer?: (value: string) => void; // Transfer calculator result to input
}

// ============ SYMBOL BAR ============
const SYMBOLS = [
  { label: 'π', value: 'π' },
  { label: '²', value: '²' },
  { label: '³', value: '³' },
  { label: '·', value: '·' },
  { label: '÷', value: '÷' },
  { label: '√', value: '√' },
  { label: '∛', value: '∛' },
];

// ============ CALCULATOR ============
function evaluateExpression(expr: string): string {
  try {
    // Replace symbols with JS-compatible operators
    let jsExpr = expr
      .replace(/,/g, '.')
      .replace(/π/g, `${Math.PI}`)
      .replace(/×/g, '*')
      .replace(/·/g, '*')
      .replace(/÷/g, '/')
      .replace(/√\(/g, 'Math.sqrt(')
      .replace(/∛\(/g, 'Math.cbrt(')
      .replace(/\^/g, '**');

    // Safety: only allow numbers, operators, parens, Math functions, dots
    if (!/^[\d\s+\-*/().^eE,Math.sqrtcb]+$/.test(jsExpr.replace(/Math\.\w+/g, ''))) {
      return 'Fehler';
    }

    // eslint-disable-next-line no-eval
    const result = Function(`"use strict"; return (${jsExpr})`)();

    if (typeof result !== 'number' || !isFinite(result)) return 'Fehler';

    // Round to reasonable precision
    const rounded = Math.round(result * 100) / 100;
    return rounded.toString().replace('.', ',');
  } catch {
    return 'Fehler';
  }
}

const CALC_BUTTONS = [
  ['7', '8', '9', '÷', '←'],
  ['4', '5', '6', '×', 'x²'],
  ['1', '2', '3', '−', 'x³'],
  ['0', ',', 'π', '+', '%'],
  ['C', '()', '√', '∛', '='],
];

export function MathKeyboard({ onInsert, onTransfer }: MathKeyboardProps) {
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcDisplay, setCalcDisplay] = useState('');
  const [calcResult, setCalcResult] = useState('');
  const calcRef = useRef<HTMLDivElement>(null);

  const handleSymbol = useCallback((value: string) => {
    onInsert(value);
  }, [onInsert]);

  const handleCalcButton = useCallback((btn: string) => {
    if (btn === 'C') {
      setCalcDisplay('');
      setCalcResult('');
      return;
    }
    if (btn === '←') {
      setCalcDisplay(prev => prev.slice(0, -1));
      setCalcResult('');
      return;
    }
    if (btn === '=') {
      const result = evaluateExpression(calcDisplay);
      setCalcResult(result);
      return;
    }
    if (btn === '()') {
      const openCount = (calcDisplay.match(/\(/g) || []).length;
      const closeCount = (calcDisplay.match(/\)/g) || []).length;
      const char = openCount > closeCount ? ')' : '(';
      setCalcDisplay(prev => prev + char);
      setCalcResult('');
      return;
    }
    if (btn === '−') {
      setCalcDisplay(prev => prev + '-');
    } else if (btn === 'x²') {
      setCalcDisplay(prev => prev + '^2');
    } else if (btn === 'x³') {
      setCalcDisplay(prev => prev + '^3');
    } else if (btn === '√') {
      setCalcDisplay(prev => prev + '√(');
    } else if (btn === '∛') {
      setCalcDisplay(prev => prev + '∛(');
    } else if (btn === '%') {
      setCalcDisplay(prev => prev + '/100');
    } else {
      setCalcDisplay(prev => prev + btn);
    }
    setCalcResult('');
  }, [calcDisplay]);

  const handleTransfer = useCallback(() => {
    if (calcResult && calcResult !== 'Fehler' && onTransfer) {
      onTransfer(calcResult);
      setCalcOpen(false);
    }
  }, [calcResult, onTransfer]);

  // Close calculator when clicking outside
  useEffect(() => {
    if (!calcOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (calcRef.current && !calcRef.current.contains(e.target as Node)) {
        setCalcOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [calcOpen]);

  return (
    <div style={{ marginTop: '8px', marginBottom: '8px' }}>
      {/* Symbol bar */}
      <div style={{
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        {SYMBOLS.map(s => (
          <button
            key={s.label}
            onClick={() => handleSymbol(s.value)}
            style={{
              width: '40px',
              height: '40px',
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              color: '#e8eaf6',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {s.label}
          </button>
        ))}
        <button
          onClick={() => setCalcOpen(!calcOpen)}
          style={{
            height: '40px',
            padding: '0 14px',
            background: calcOpen ? 'rgba(124, 77, 255, 0.3)' : 'rgba(124, 77, 255, 0.15)',
            border: `2px solid ${calcOpen ? '#b388ff' : 'rgba(124, 77, 255, 0.4)'}`,
            borderRadius: '8px',
            color: calcOpen ? '#e8eaf6' : '#b388ff',
            fontSize: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Calculator size={14} />
          {calcOpen ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
        </button>
      </div>

      {/* Calculator */}
      {calcOpen && (
        <div ref={calcRef} style={{
          marginTop: '8px',
          background: '#0f1535',
          border: '1px solid rgba(124, 77, 255, 0.3)',
          borderRadius: '12px',
          padding: '12px',
          maxWidth: '380px',
        }}>
          {/* Display */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.06)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '10px',
            minHeight: '52px',
          }}>
            <div style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '16px',
              minHeight: '22px',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}>
              {calcDisplay || '0'}
            </div>
            {calcResult && (
              <div style={{
                color: calcResult === 'Fehler' ? '#ef5350' : '#ffd54f',
                fontSize: '24px',
                fontWeight: 700,
                fontFamily: 'monospace',
                marginTop: '2px',
              }}>
                = {calcResult}
              </div>
            )}
          </div>

          {/* Buttons grid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {CALC_BUTTONS.map((row, ri) => (
              <div key={ri} style={{ display: 'flex', gap: '6px' }}>
                {row.map(btn => {
                  const isOp = ['÷', '×', '−', '+', '=', '^'].includes(btn);
                  const isFunc = ['x²', 'x³', '√', '∛', '%'].includes(btn);
                  const isClear = btn === 'C';
                  const isBackspace = btn === '←';
                  return (
                    <button
                      key={btn}
                      onClick={() => handleCalcButton(btn)}
                      style={{
                        flex: 1,
                        height: '52px',
                        background: isClear || isBackspace
                          ? 'rgba(239, 83, 80, 0.15)'
                          : isFunc
                            ? 'rgba(0, 188, 212, 0.15)'
                            : isOp
                              ? 'rgba(124, 77, 255, 0.2)'
                              : 'rgba(255, 255, 255, 0.06)',
                        border: `1px solid ${isClear || isBackspace
                          ? 'rgba(239, 83, 80, 0.3)'
                          : isFunc
                            ? 'rgba(0, 188, 212, 0.3)'
                            : isOp
                              ? 'rgba(124, 77, 255, 0.3)'
                              : 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '10px',
                        color: isClear || isBackspace ? '#ef5350' : isFunc ? '#00bcd4' : isOp ? '#b388ff' : '#e8eaf6',
                        fontSize: '18px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {btn}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Transfer button */}
          {calcResult && calcResult !== 'Fehler' && onTransfer && (
            <button
              onClick={handleTransfer}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                width: '100%',
                height: '52px',
                marginTop: '8px',
                background: 'rgba(76, 175, 80, 0.15)',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                borderRadius: '10px',
                color: '#4caf50',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Check size={16} /> {calcResult} übernehmen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
